import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    UserCircleIcon,
    ArrowLeftEndOnRectangleIcon,
    ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useLogout } from "../../api/useLogout";
import { Facility } from "../../pages/settings/facilities/useFacility";
import { useStateContext } from "../../contexts/ContextProvider";

interface SidebarUserMenuProps {
    expanded: boolean;
    facility: Facility | null | undefined;
}

export default function SidebarUserMenu({
    expanded,
    facility,
}: SidebarUserMenuProps) {
    const { user } = useStateContext();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { logout } = useLogout();

    // Fermer au clic extérieur
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    // Générer les initiales de l'utilisateur
    const getInitials = () => {
        const first = user?.firstname?.charAt(0)?.toUpperCase() || '';
        const last = user?.lastname?.charAt(0)?.toUpperCase() || '';
        return first + last || 'U';
    };

    return (
        <div className="relative px-2" ref={dropdownRef}>
            <button
                ref={buttonRef}
                onClick={handleToggle}
                className={`w-full flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-100 ${
                    expanded ? "justify-start" : "justify-center"
                }`}
            >
                {/* Avatar avec initiales */}
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-main-color-light to-main-color/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-main-color">
                        {getInitials()}
                    </span>
                </div>

                {/* Infos utilisateur */}
                {expanded && (
                    <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-gray-800 truncate">
                            {user?.firstname} {user?.lastname}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                            {facility?.name || 'Mon compte'}
                        </p>
                    </div>
                )}
            </button>

            {isOpen && (
                <div
                    className={`absolute bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 min-w-[280px] ${
                        expanded
                            ? "bottom-full mb-1 left-0"
                            : "left-full ml-2 bottom-0"
                    }`}
                >
                    {/* Informations utilisateur */}
                    <div className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900 truncate">
                            {user.firstname} {user.lastname}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                            {user.email}
                        </p>
                    </div>

                    <div className="border-t border-gray-200 my-1" />

                    {/* Lien Sécurité (double authentification) */}
                    <Link
                        to="/account/security"
                        onClick={() => setIsOpen(false)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <ShieldCheckIcon className="w-4 h-4 text-gray-400" />
                        <span>Sécurité</span>
                    </Link>

                    {/* Bouton Déconnexion */}
                    <button
                        onClick={logout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <ArrowLeftEndOnRectangleIcon className="w-4 h-4 text-gray-400" />
                        <span>Déconnexion</span>
                    </button>
                </div>
            )}
        </div>
    );
}
